import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Archive,
  ArrowCounterClockwise,
  CalendarBlank,
  ChatCircleDots,
  Clock,
  PaperPlaneTilt,
  ShieldCheck,
  ShieldWarning,
  SignOut,
  Spinner,
  Trash,
  VideoCamera,
} from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/errors";

function ago(iso) {
  if (!iso) return "—";
  const d = new Date(iso).getTime();
  const diff = (Date.now() - d) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function fmtDateTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function ThreadRow({ thread: t, view, onArchive, onUnarchive, onDelete }) {
  const isArchived = view === "archived";
  const stop = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  return (
    <div className="relative group">
      <Link
        to={`/coach/thread/${t.id}`}
        data-testid={`dashboard-thread-${t.id}`}
        className="block bg-white border border-[#E8E4DB] rounded-2xl p-5 hover:border-[#1E3A2B]/50 hover:shadow-sm transition-all"
      >
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            {t.unread && !isArchived && (
              <span
                className="w-2.5 h-2.5 rounded-full bg-[#C77D5E] flex-shrink-0 mt-1.5"
                data-testid="thread-unread-dot"
              />
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-[#121815]">
                  {t.leader_name || t.leader_email || "Anonymous leader"}
                </span>
                {t.scheduled_at && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#1E3A2B]/10 text-[#1E3A2B] text-[11px] font-medium">
                    {t.scheduled_medium === "audio" ? (
                      <Clock size={11} weight="duotone" />
                    ) : (
                      <VideoCamera size={11} weight="duotone" />
                    )}
                    {fmtDateTime(t.scheduled_at)}
                  </span>
                )}
                {isArchived && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#8A948E]/15 text-[#4A524C] text-[11px] font-medium">
                    Archived
                  </span>
                )}
              </div>
              <p className="text-sm text-[#4A524C] mt-1 line-clamp-2 pr-24">
                {t.preview || <span className="italic text-[#8A948E]">No preview</span>}
              </p>
            </div>
          </div>
          <div className="text-xs text-[#8A948E] flex-shrink-0 flex items-center gap-1">
            <PaperPlaneTilt size={12} weight="duotone" />
            {ago(t.last_user_message_at || t.last_coach_message_at || t.created_at)}
          </div>
        </div>
      </Link>

      {/* Inline actions — always visible on touch, fade-in on hover for desktop */}
      <div className="absolute right-4 bottom-4 flex items-center gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
        {isArchived ? (
          <button
            onClick={(e) => {
              stop(e);
              onUnarchive(t);
            }}
            data-testid={`thread-unarchive-${t.id}`}
            aria-label="Unarchive thread"
            title="Unarchive"
            className="p-2 rounded-lg bg-white border border-[#E8E4DB] text-[#4A524C] hover:text-[#1E3A2B] hover:border-[#1E3A2B]"
          >
            <ArrowCounterClockwise size={15} weight="duotone" />
          </button>
        ) : (
          <button
            onClick={(e) => {
              stop(e);
              onArchive(t);
            }}
            data-testid={`thread-archive-${t.id}`}
            aria-label="Archive thread"
            title="Archive"
            className="p-2 rounded-lg bg-white border border-[#E8E4DB] text-[#4A524C] hover:text-[#1E3A2B] hover:border-[#1E3A2B]"
          >
            <Archive size={15} weight="duotone" />
          </button>
        )}
        <button
          onClick={(e) => {
            stop(e);
            onDelete(t);
          }}
          data-testid={`thread-delete-${t.id}`}
          aria-label="Delete thread"
          title="Delete permanently"
          className="p-2 rounded-lg bg-white border border-[#E8E4DB] text-[#8A948E] hover:text-[#C77D5E] hover:border-[#C77D5E]"
        >
          <Trash size={15} weight="duotone" />
        </button>
      </div>
    </div>
  );
}

export default function CoachDashboard() {
  const { coach, signOutCoach } = useAuth();
  const [threads, setThreads] = useState([]);
  const [counts, setCounts] = useState({ active: 0, archived: 0 });
  const [pendingApproval, setPendingApproval] = useState(false);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("active"); // active | archived
  const [filter, setFilter] = useState("all"); // all | unread | scheduled (applies within active view)

  const load = useCallback(async (currentView) => {
    try {
      const { data } = await api.get(`/coach/dashboard/threads?view=${currentView}`);
      setThreads(data.threads || []);
      if (data.counts) setCounts(data.counts);
      setPendingApproval(!!data.pending_approval);
    } catch (err) {
      toast.error(apiError(err, "Could not load threads"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(view);
    const id = setInterval(() => load(view), 15000);
    return () => clearInterval(id);
  }, [view, load]);

  const handleArchive = async (t) => {
    try {
      await api.post(`/coach/dashboard/threads/${t.id}/archive`);
      setThreads((prev) => prev.filter((x) => x.id !== t.id));
      setCounts((c) => ({ active: Math.max(0, c.active - 1), archived: c.archived + 1 }));
      toast.success("Archived.", {
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post(`/coach/dashboard/threads/${t.id}/unarchive`);
              toast.success("Restored.");
              load(view);
            } catch (err) {
              toast.error(apiError(err, "Could not undo"));
            }
          },
        },
      });
    } catch (err) {
      toast.error(apiError(err, "Could not archive"));
    }
  };

  const handleUnarchive = async (t) => {
    try {
      await api.post(`/coach/dashboard/threads/${t.id}/unarchive`);
      setThreads((prev) => prev.filter((x) => x.id !== t.id));
      setCounts((c) => ({ active: c.active + 1, archived: Math.max(0, c.archived - 1) }));
      toast.success("Moved to Inbox.", {
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post(`/coach/dashboard/threads/${t.id}/archive`);
              toast.success("Archived again.");
              load(view);
            } catch (err) {
              toast.error(apiError(err, "Could not undo"));
            }
          },
        },
      });
    } catch (err) {
      toast.error(apiError(err, "Could not unarchive"));
    }
  };

  const handleDelete = async (t) => {
    // No confirm dialog — a 60s Undo gives a better, less-interrupting safety net.
    // Soft-delete on the server: the TTL index actually purges after the grace window.
    try {
      const { data } = await api.delete(`/coach/dashboard/threads/${t.id}`);
      setThreads((prev) => prev.filter((x) => x.id !== t.id));
      setCounts((c) =>
        view === "archived"
          ? { ...c, archived: Math.max(0, c.archived - 1) }
          : { ...c, active: Math.max(0, c.active - 1) }
      );
      const grace = (data?.grace_seconds || 60) * 1000;
      toast.success("Conversation deleted.", {
        duration: grace,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post(`/coach/dashboard/threads/${t.id}/restore`);
              toast.success("Restored.");
              load(view);
            } catch (err) {
              toast.error(apiError(err, "Undo window expired"));
            }
          },
        },
      });
    } catch (err) {
      toast.error(apiError(err, "Could not delete"));
    }
  };

  const visible = useMemo(() => {
    if (view === "archived") return threads; // no sub-filter when viewing archived
    if (filter === "unread") return threads.filter((t) => t.unread);
    if (filter === "scheduled") return threads.filter((t) => t.scheduled_at);
    return threads;
  }, [threads, filter, view]);

  const unreadCount = threads.filter((t) => t.unread).length;
  const scheduledCount = threads.filter((t) => t.scheduled_at).length;

  if (coach === null) {
    return (
      <div className="text-center text-[#8A948E] py-16 flex items-center justify-center gap-2">
        <Spinner size={16} className="animate-spin" /> Loading…
      </div>
    );
  }
  if (!coach) return <Navigate to="/coach/login" replace />;

  return (
    <div className="max-w-5xl mx-auto px-6 sm:px-8 py-12 lg:py-16">
      <div className="anim-in delay-1 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="label-kicker" style={{ color: "#1E3A2B" }}>Coach Console</div>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#1E3A2B] mt-3 tracking-tight leading-tight">
            {coach.name?.split(" ")[0] || "Welcome"}.
          </h1>
          <p className="text-[#4A524C] mt-3 leading-relaxed">Every conversation, in one place.</p>
        </div>
        <div className="flex items-center gap-2">
          {coach.role === "admin" && (
            <Link
              to="/coach/admin"
              data-testid="dashboard-admin-link"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#1E3A2B] text-white hover:bg-[#2C4C3B] text-sm font-medium"
            >
              <ShieldCheck size={16} weight="duotone" /> Admin
            </Link>
          )}
          <Link
            to="/coach/availability"
            data-testid="dashboard-availability-link"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-[#1E3A2B] text-[#1E3A2B] hover:bg-[#F3F0EA] text-sm font-medium"
          >
            <CalendarBlank size={16} weight="duotone" /> Availability
          </Link>
          <button
            onClick={signOutCoach}
            data-testid="dashboard-signout"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-[#8A948E] hover:text-[#1E3A2B] hover:bg-[#F3F0EA] text-sm"
          >
            <SignOut size={16} weight="duotone" /> Sign out
          </button>
        </div>
      </div>

      {pendingApproval && (
        <div
          data-testid="dashboard-pending-banner"
          className="mt-8 bg-[#FAEFE8] border border-[#C77D5E]/40 rounded-2xl px-5 py-4 anim-in delay-2"
        >
          <div className="flex items-start gap-3">
            <ShieldWarning size={22} weight="duotone" className="text-[#C77D5E] flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-[#1E3A2B]">Waiting for admin approval.</div>
              <p className="text-sm text-[#4A524C] mt-1 leading-relaxed">
                Your coach account has been created. An administrator will review your
                application — once approved, leaders assigned to you will appear here.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Primary view toggle: Active vs. Archived */}
      <div className="mt-8 flex items-center gap-2 flex-wrap anim-in delay-2">
        <button
          onClick={() => {
            setView("active");
            setFilter("all");
          }}
          data-testid="dashboard-view-active"
          className={`px-4 py-2 rounded-full text-sm transition-all ${
            view === "active"
              ? "bg-[#1E3A2B] text-white"
              : "bg-white text-[#4A524C] border border-[#E8E4DB] hover:border-[#1E3A2B]"
          }`}
        >
          Inbox · {counts.active}
        </button>
        <button
          onClick={() => setView("archived")}
          data-testid="dashboard-view-archived"
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm transition-all ${
            view === "archived"
              ? "bg-[#1E3A2B] text-white"
              : "bg-white text-[#4A524C] border border-[#E8E4DB] hover:border-[#1E3A2B]"
          }`}
        >
          <Archive size={14} weight="duotone" />
          Archived · {counts.archived}
        </button>
      </div>

      {/* Sub-filters only apply to the active inbox view */}
      {view === "active" && (
        <div className="mt-3 flex items-center gap-2 flex-wrap anim-in delay-3">
          {[
            { v: "all", label: `All · ${threads.length}` },
            { v: "unread", label: `Unread · ${unreadCount}` },
            { v: "scheduled", label: `Scheduled · ${scheduledCount}` },
          ].map((o) => (
            <button
              key={o.v}
              onClick={() => setFilter(o.v)}
              data-testid={`dashboard-filter-${o.v}`}
              className={`px-3 py-1.5 rounded-full text-xs transition-all ${
                filter === o.v
                  ? "bg-[#F3F0EA] text-[#1E3A2B] border border-[#1E3A2B]/30"
                  : "bg-transparent text-[#8A948E] hover:text-[#1E3A2B]"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}

      <section className="mt-6 anim-in delay-3">
        {loading ? (
          <div className="text-[#8A948E] text-sm flex items-center gap-2">
            <Spinner size={14} className="animate-spin" /> Loading conversations…
          </div>
        ) : visible.length === 0 ? (
          <div className="text-center text-[#8A948E] bg-[#F3F0EA] rounded-2xl px-6 py-12">
            <ChatCircleDots size={36} weight="duotone" className="mx-auto text-[#1E3A2B]/40 mb-3" />
            <div className="font-medium text-[#4A524C]">
              {view === "archived"
                ? "No archived conversations."
                : "No conversations match this filter yet."}
            </div>
            {view !== "archived" && (
              <div className="text-sm mt-1">When a leader opens a thread, it'll show up here.</div>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {visible.map((t) => (
              <ThreadRow
                key={t.id}
                thread={t}
                view={view}
                onArchive={handleArchive}
                onUnarchive={handleUnarchive}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
